import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  signal,
  WritableSignal,
  effect,
  untracked,
} from '@angular/core';

import { Subscription } from 'rxjs';
import { throwError } from 'rxjs';
import { LazyLoadEvent } from 'primeng/api';
import { catchError } from 'rxjs/operators';
import { QueryParams } from '../core/http/query-params';
import {
  customersSavedViewDeserializer as savedViewDeserializer,
  createGridSerializer,
} from '@/app/customer/customer-utils';

import { broadcastError } from '@/app/core/toasts/broadcast-toast';

import {
  ActiveFilterService,
  ColumnDefinitionService,
  MetricsGridConfig,
  BreadcrumbService,
  FormatService,
  GridConfigService,
  IGridColumnGroup,
  ClientProfileService,
  GridTableNgComponent,
  GridSortingService,
} from '../core';

import { HeaderService } from '../header';
import { CustomerService } from './customer.service';
import { DownloadsService } from '../downloads';
import { PageOptionsNg, SortOptionsNg, SortOptions } from '../models';
import { AuthenticationService } from '../core/authentication/authentication.service';
import { Router } from '@angular/router';
import { FilterProfileUtilsService } from '@/app/filter/profiles/filter-profile-utils.service';
import { SavedViewsService } from '@/app/modules/saved-views/saved-views.service';
import { TranslateService } from '@ngx-translate/core';
import { SavedView } from '@/app/modules/saved-views/types';
import { FeatureFlagService } from '@/app/core/feature-flags/feature-flag.service';

@Component({
  selector: 'rdo-customers',
  templateUrl: 'customers.component.html',
  styleUrls: ['customer.component.scss'],
  standalone: false,
})
export class CustomersComponent implements OnInit, OnDestroy {
  constructor(
    private authenticationService: AuthenticationService,
    private filterService: ActiveFilterService,
    private customerService: CustomerService,
    private columnService: ColumnDefinitionService,
    private breadcrumbService: BreadcrumbService,
    private downloadsService: DownloadsService,
    private headerService: HeaderService,
    private formatService: FormatService,
    private gridConfigService: GridConfigService,
    private clientProfileService: ClientProfileService,
    private gridSortingService: GridSortingService,
    private router: Router,
    private filterProfileUtilsService: FilterProfileUtilsService,
    private savedViewsService: SavedViewsService,
    public translateService: TranslateService,
    protected featureFlagService: FeatureFlagService
  ) {
    // React to saved view changes - track the view NAME to prevent infinite loops
    effect(() => {
      const areSavedViewsEnabled =
        this.featureFlagService.isActive('flagSavedViews');
      if (!areSavedViewsEnabled) {
        return;
      }
      const selectedView = this.savedViewsService.getSelectedView();
      const viewName = selectedView?.name ?? null;

      // We don't want the effect to re-run when lastAppliedViewName changes
      const lastApplied = untracked(() => this.lastAppliedViewName());
      if (viewName === lastApplied) {
        return;
      }

      // Use untracked to read gridConfig
      const hasGridConfig = untracked(() => !!this.gridConfig());

      if (selectedView && hasGridConfig) {
        // Set lastAppliedViewName FIRST, before any async work, to prevent re-entry
        this.lastAppliedViewName.set(viewName);
        // Call handler in untracked context to avoid tracking any signal reads inside
        untracked(async () => {
          const currentConfig = this.gridConfig();

          // Define a callback to extract visible column names for logging
          const getVisibleColumns = (config: MetricsGridConfig) => {
            return (
              config?.groups
                ?.filter((g) => g.visible)
                .map((g) => g.columnSelectorTitle) || []
            );
          };

          // Apply the saved view changes to the grid config
          const response = await this.savedViewsService.handleSavedViewChange(
            selectedView,
            currentConfig,
            savedViewDeserializer,
            getVisibleColumns
          );

          if (!response || !response.config) {
            console.error(
              '[EFFECT] No config returned from handleSavedViewChange, aborting'
            );
            return;
          }

          // Update the signal - this will automatically trigger change detection
          this.gridConfig.set(response.config);

          // Update the gridSortingService - this is the single source of truth
          // The sorting getter will automatically read from this service
          this.gridSortingService.setSortOption(
            response.sorting.key,
            response.sorting.direction === 'desc' // setSortOption expects descending as boolean
          );

          // Update local sorting for template binding (avoids getter performance issues)
          this.sorting = this.gridSortingService.getSortOptionsNg();

          this.paging.page = 1;
          if (this.grid) {
            this.grid.resetPaginator();
          }

          // Grid config is now applied with correct filters already in place — load data
          this.load();

          // Signal that the full saved view application is complete
          this.savedViewsService.notifyViewApplied();
        });
      }
    });
  }
  loading: WritableSignal<boolean> = signal(true);
  isLoaded = signal(false);
  areFiltersInitialized = signal(false);
  customers: any;
  gridConfig: WritableSignal<MetricsGridConfig | null> = signal(null);
  private lastAppliedViewName: WritableSignal<string | null> = signal(null);
  frozenRows: any;
  paging = new PageOptionsNg();
  sorting: SortOptionsNg; // Still needed for template binding, but updated only when sorting actually changes
  totalCount = 0;
  frozenWidth: string;
  gridName = 'CUSTOMERS_GRID';
  calculatedProductTypeLength = 175;
  private subscriptions: Array<Subscription> = [];
  @ViewChild('grid') grid: GridTableNgComponent;
  customersRequestInProgress: Subscription;
  ngOnInit(): void {
    const areSavedViewsEnabled =
      this.featureFlagService.isActive('flagSavedViews');
    if (areSavedViewsEnabled) {
      this.savedViewsService.initGrid(this.gridName, this.getSerializer());
    }
    this.authenticationService.startSentryTransaction(
      this.router.routerState.snapshot.url
    );
    this.gridSortingService.setGridName(this.gridName);

    // Initialize sorting from the service (only once)
    this.sorting = this.gridSortingService.getSortOptionsNg();

    this.breadcrumbService.update([
      { title: 'main.core.common.counts.customers.all', class: 'active' },
    ]);

    this.clientProfileService.getClientAttributes().subscribe((attributes) => {
      this.beginLoad();
    });

    this.subscribeToFilters();

    this.subscriptions.push(
      this.authenticationService.userInfoView.subscribe((userInfo) => {
        const config = this.gridConfig();
        if (config) {
          this.gridConfig.update((c) => {
            if (c) {
              c.enableExcelExport = userInfo.HasClientAccessToExportData;
            }
            return c;
          });
        }
      })
    );
  }

  private getSerializer = createGridSerializer({
    gridName: this.gridName,
    gridConfig: this.gridConfig,
    gridSortingService: this.gridSortingService,
    componentName: 'customers.component',
  });

  beginLoad = async () => {
    const areSavedViewsEnabled =
      this.featureFlagService.isActive('flagSavedViews');
    if (areSavedViewsEnabled) {
      await this.savedViewsService.load();
    }

    this.setProductTypeWidth();

    // Only call configureGrid on initial load
    await this.configureGrid(true);

    // After initial configuration, set the lastAppliedViewName to prevent re-applying
    const currentView = this.savedViewsService.getSelectedView();
    if (currentView) {
      this.lastAppliedViewName.set(currentView.name);

      // Validate that the initial grid configuration matches the selected view
      // this.validateGridConfigMatchesView(
      //   this.gridConfig(),
      //   currentView,
      //   'AFTER-CONFIGURE-GRID'
      // );
    }

    this.load(true);
  };

  ngOnDestroy() {
    while (this.subscriptions && this.subscriptions.length > 0) {
      this.subscriptions.pop().unsubscribe();
    }
  }

  load = (initEvent?: boolean) => {
    if (this.customersRequestInProgress) {
      this.customersRequestInProgress.unsubscribe();
    }
    this.isLoaded.set(false);
    this.loading.set(true);
    const params = new QueryParams();
    const config = this.gridConfig();
    if (config) {
      if (
        config.groups[1].visible ||
        (initEvent && this.isStoredValueVisible(1))
      ) {
        params.set('groupBySalesRep', 'true');
      }
      if (
        config.groups[2].visible ||
        (initEvent && this.isStoredValueVisible(2))
      ) {
        params.set('groupByProductType', 'true');
      }
    }
    if (this.filterService.getCurrentFilter().useRouseSchema) {
      params.set('useRouseSchema', 'true');
    }
    this.customersRequestInProgress = this.customerService
      .getCustomers(this.paging, this.getSorting(), params)
      .subscribe((x) => {
        this.loading.set(false);
        this.isLoaded.set(true);
        if (x.Items && x.Items.length > 0) {
          this.customers = x.Items;
        } else {
          this.customers = [];
        }

        this.totalCount = x.TotalCount;
        this.authenticationService.finishSentryTransaction();
      });
  };

  private subscribeToFilters() {
    this.subscriptions.push(
      this.filterService.filterChange.subscribe(async () => {
        // If saved views is currently applying a view or its filters, skip —
        // the saved view effect will handle configureGrid + load.

        /*
        if (this.savedViewsService.isApplying()) {
          return;
        }
*/

        this.paging.page = 1;
        if (this.grid) {
          this.grid.resetPaginator();
        }

        // Deal with the rented as product type vs product type column title
        const currentFilters = this.filterService.getCurrentFilter();

        const config = this.gridConfig();
        if (!config) {
          return;
        }

        const productTypesColumnIndex = config.groups.findIndex(
          (group) => group.id === 'product-type'
        );

        // Guard against group not found
        /*if (productTypesColumnIndex === -1) {
          await this.configureGrid(true);
          this.load(true);
          return;
        }*/

        let updateProductTypesText = false;
        if (currentFilters.useRentedAsProductTypes) {
          // If the "Rented as Product Types" filter is enabled, we want to show the Product Type column
          if (
            !config.groups[
              productTypesColumnIndex
            ].columnSelectorTitle.includes('rented_as_product_types')
          ) {
            updateProductTypesText = true;
          }
        } else {
          if (
            config.groups[productTypesColumnIndex].columnSelectorTitle.includes(
              'rented_as_product_types'
            )
          ) {
            updateProductTypesText = true;
          }
        }

        if (updateProductTypesText) {
          // Use update() with a cloned object to ensure Angular signal change detection
          // and preserve the MetricsGridConfig class methods
          this.gridConfig.update((currentConfig) => {
            if (!currentConfig) {
              return currentConfig;
            }
            const clonedConfig = currentConfig.clone();
            const newHeading = this.getProductTypeHeading();
            clonedConfig.groups[productTypesColumnIndex].columnSelectorTitle =
              newHeading;
            clonedConfig.groups[productTypesColumnIndex].columns[0].title =
              newHeading;
            return clonedConfig;
          });
          // Only load data - don't call configureGrid which would reset visibility
          this.load(true);
        } else {
          await this.configureGrid(true);
          this.load(true);
        }
      })
    );
  }

  isStoredValueVisible(index: number): boolean {
    return (
      this.gridConfigService.getConfig(this.gridName) &&
      this.gridConfigService.getConfig(this.gridName)[index] &&
      this.gridConfigService.getConfig(this.gridName)[index].visible
    );
  }

  visibilityChanged = (event: IGridColumnGroup) => {
    const aux = event;

    this.gridConfig.update((config) => {
      if (!config) {
        return config;
      }

      if (
        event.columnSelectorTitle === 'main.core.common.counts.sales_reps.count'
      ) {
        config.groups[3].visible = !event.visible;
      }
      config.groups[0].columns[0].isGroupingEnabled =
        config.groups[1].visible || config.groups[2].visible;

      if (
        event.columnSelectorTitle ===
          'main.core.common.counts.sales_reps.count' ||
        event.columnSelectorTitle === this.getProductTypeHeading()
      ) {
        this.paging.page = 1;
        this.grid.resetPaginator();
        // Flag the grid as dirty so that the saved view service knows to save the new state
        config.isDirty = true;
        this.load();
      }

      return config;
    });

    this.savedViewsService.markDirty();
  };

  changePage = (event: any) => {
    if (event.rows !== this.paging.pageSize) {
      this.paging.pageSize = event.rows;
    }

    this.paging.page = event.first < 1 ? 1 : event.first / event.rows + 1;
    this.load();
  };

  changeLazyLoad = (event: LazyLoadEvent) => {
    // Check if sorting actually changed
    if (
      this.sorting.sortField === event.sortField &&
      this.sorting.sortOrder === event.sortOrder
    ) {
      return;
    }

    // Update the service - this is the single source of truth
    this.gridSortingService.setSortOption(
      event.sortField,
      event.sortOrder === -1
    );

    // Update local sorting for template binding (avoids getter performance issues)
    this.sorting = this.gridSortingService.getSortOptionsNg();

    this.load();
  };

  exportExcel = (args: any) => {
    this.loading.set(true);
    const config = this.gridConfig();
    if (!config) {
      return;
    }

    const translatedConfig = config.cloneAndTranslate((text) =>
      this.formatService.translateAndFormat(text, false)
    );
    this.customerService
      .getCustomersDownload(this.getSorting(), translatedConfig)
      .pipe(
        catchError((error, obs) => {
          this.loading.set(false);
          broadcastError(
            error.message,
            this.translateService.instant(
              'main.core.common.errors.something_went_wrong'
            )
          );
          return throwError(error);
        })
      )
      .subscribe((blob) => {
        this.loading.set(false);
        this.downloadsService.saveExcelBlob(blob);
      });
  };

  protected getProductTypeHeading(): string {
    return this.filterProfileUtilsService
      .getProductTypesHeader()
      .ambiguousCount();
  }

  /**
   * Validates that the gridConfig matches the SavedView configuration
   */
  private validateGridConfigMatchesView(
    config: MetricsGridConfig | null,
    view: SavedView,
    context: string
  ): void {
    if (!config || !view) {
      console.log(`[VALIDATION ${context}] Skipping - config or view is null`);
      return;
    }

    const errors: string[] = [];
    const visibleMatchingRows: Array<{
      columnSelectorTitle: string;
      status: string;
    }> = [];
    const mismatchRows: Array<{
      columnSelectorTitle: string;
      expected: string;
      actual: string;
    }> = [];
    let correctlyHiddenCount = 0;

    // Build table data and check for mismatches
    config.groups.forEach((group, index) => {
      const shouldBeVisible = view.columns.includes(group.columnSelectorTitle);
      const isVisible = group.visible;
      const match = shouldBeVisible === isVisible;

      if (match) {
        if (isVisible) {
          // Only track visible columns that match
          visibleMatchingRows.push({
            columnSelectorTitle: group.columnSelectorTitle,
            status: 'VISIBLE ✓',
          });
        } else {
          // Count correctly hidden columns but don't display them
          correctlyHiddenCount++;
        }
      } else {
        mismatchRows.push({
          columnSelectorTitle: group.columnSelectorTitle,
          expected: shouldBeVisible ? 'VISIBLE' : 'HIDDEN',
          actual: isVisible ? 'VISIBLE' : 'HIDDEN',
        });
        const err = `Group ${index} "${group.columnSelectorTitle}": expected ${shouldBeVisible ? 'VISIBLE' : 'HIDDEN'}, actual ${isVisible ? 'VISIBLE' : 'HIDDEN'}`;
        errors.push(err);
      }
    });

    // Print the validation results
    console.log('\n' + '═'.repeat(120));
    console.log(`[VALIDATION ${context}] SavedView: "${view.name}"`);
    console.log('═'.repeat(120));

    // Show summary first
    const totalColumns = config.groups.length;
    const visibleMatchCount = visibleMatchingRows.length;
    const mismatchCount = mismatchRows.length;

    if (errors.length === 0) {
      console.log(
        `✅ ALL COLUMNS MATCH: ${totalColumns}/${totalColumns} columns correctly configured`
      );
      console.log(
        `   └─ ${visibleMatchCount} visible, ${correctlyHiddenCount} hidden`
      );
    } else {
      console.error(
        `❌ MISMATCHES FOUND: ${mismatchCount}/${totalColumns} columns incorrectly configured`
      );
      console.log(
        `   └─ ${visibleMatchCount} visible (correct), ${correctlyHiddenCount} hidden (correct), ${mismatchCount} incorrect`
      );
    }
    console.log('─'.repeat(120));

    // Show mismatches first if any
    if (mismatchRows.length > 0) {
      console.log('\n🔴 MISMATCHED COLUMNS:');
      const col1Width = Math.max(
        'Column Selector Title'.length,
        ...mismatchRows.map((r) => r.columnSelectorTitle.length)
      );
      const col2Width = 'Expected'.length;
      const col3Width = 'Actual'.length;

      console.log(
        `│ ${'Column Selector Title'.padEnd(col1Width)} │ ${'Expected'.padEnd(col2Width)} │ ${'Actual'.padEnd(col3Width)} │`
      );
      console.log(
        '├' +
          '─'.repeat(col1Width + 2) +
          '┼' +
          '─'.repeat(col2Width + 2) +
          '┼' +
          '─'.repeat(col3Width + 2) +
          '┤'
      );

      mismatchRows.forEach((row) => {
        console.log(
          `│ ❌ ${row.columnSelectorTitle.padEnd(col1Width - 2)} │ ${row.expected.padEnd(col2Width)} │ ${row.actual.padEnd(col3Width)} │`
        );
      });
      console.log('─'.repeat(120));
    }

    // Show only VISIBLE matching columns
    if (visibleMatchingRows.length > 0) {
      console.log(`\n✅ VISIBLE COLUMNS (${visibleMatchingRows.length}):`);
      visibleMatchingRows.forEach((row, idx) => {
        console.log(`   ${idx + 1}. ${row.columnSelectorTitle}`);
      });
    }

    console.log('═'.repeat(120) + '\n');
  }

  private setProductTypeWidth(): void {
    this.calculatedProductTypeLength =
      this.clientProfileService.txAttributes.MaxProdTypeLen * 7.25;
    this.calculatedProductTypeLength =
      this.calculatedProductTypeLength < 175
        ? 175
        : this.calculatedProductTypeLength;
  }

  private configureGrid = async (initEvent?: boolean) => {
    // Capture current visibility settings before recreating config, keyed by id or columnSelectorTitle
    const existingConfig = this.gridConfig();
    const existingVisibilityMap = new Map<string, boolean>();
    if (existingConfig?.groups) {
      existingConfig.groups.forEach((g) => {
        const key = g.id || g.columnSelectorTitle;
        if (key) {
          existingVisibilityMap.set(key, g.visible ?? false);
        }
      });
    }

    const customerNameWidth = 225; // TODO: This is super strange.
    const salesRepCountColumn = this.columnService.SalesRepCount();
    if (
      initEvent &&
      this.gridSortingService.getSortFieldOrDefault() === 'SalesRepCount'
    ) {
      salesRepCountColumn.width += 15;
    }

    const transactionCountColumn = this.columnService.TransactionCount();
    if (
      initEvent &&
      this.gridSortingService.getSortFieldOrDefault() === 'TransactionCount'
    ) {
      transactionCountColumn.width += 15;
    }
    this.frozenWidth = `${customerNameWidth + (salesRepCountColumn.width || 110) + (transactionCountColumn.width || 110)}px`;

    const config = new MetricsGridConfig(
      [
        {
          title: '',
          visible: true,
          locked: true,
          columnSelectorTitle: 'main.core.common.counts.customers.singular',
          columns: [
            {
              title: 'main.core.common.counts.customers.singular',
              field: 'CustomerName',
              sortColumn: 'CustomerName',
              width: 100,
              isGroupingEnabled: true,
              isString: true,
              autoWidth: true,
              maxWidth: 255,
              headerStyle: {
                'text-align': 'left',
              },
              cellStyle: {
                'text-align': 'left',
              },
              linkDsl: (v, r) => {
                const sorting = this.getSorting();
                const params = {
                  sortOn: sorting.sortOn,
                  desc: sorting.descending,
                };
                return ['.', r.CustomerId, params];
              },
            },
          ],
        },
        {
          id: 'sales-rep-name',
          title: '',
          visible: false,
          locked: true,
          forceCanClose: true,
          keyColumn: true,
          columnSelectorTitle: 'main.core.common.counts.sales_reps.count',
          columns: [
            {
              title: 'main.core.common.counts.sales_reps.count',
              field: 'SalesRepName',
              sortColumn: 'SalesRepName',
              headerStyle: {
                'text-align': 'left',
              },
              cellStyle: {
                'text-align': 'left',
              },
              width: 100,
              maxWidth: 180,
              autoWidth: true,
            },
          ],
        },
        {
          id: 'product-type',
          title: '',
          visible: false,
          locked: true,
          forceCanClose: true,
          keyColumn: true,
          columnSelectorTitle: this.getProductTypeHeading(),
          columns: [
            {
              title: this.getProductTypeHeading(),
              field: 'ProductTypeDescription',
              sortColumn: 'ProductTypeDescription',
              headerStyle: {
                'text-align': 'left',
              },
              cellStyle: {
                'text-align': 'left',
              },
              width: 100,
              maxWidth: 500,
              minWidth: 115,
              autoWidth: true,
            },
          ],
        },
        {
          id: 'sales-rep-count',
          title: '',
          visible: true,
          locked: true,
          columnSelectorTitle: 'main.core.common.counts.sales_reps.count',
          columns: [salesRepCountColumn],
        },
        {
          title: '',
          visible: true,
          locked: true,
          columnSelectorTitle: 'main.core.common.counts.transactions.count',
          columns: [transactionCountColumn],
        },
        ...this.columnService.StandardMetricSetColumnGroups(true),
      ],
      null,
      true
    );
    // Read session/localStorage and apply stored column visibility so isStoredValueVisible() works on initial load
    this.gridConfigService.applyVisibilityOptions(this.gridName, config);

    // Restore visibility settings from existing config (if any) to preserve saved view state
    if (existingVisibilityMap.size > 0) {
      config.groups.forEach((group) => {
        const key = group.id || group.columnSelectorTitle;
        if (key && existingVisibilityMap.has(key)) {
          group.visible = existingVisibilityMap.get(key);
        }
      });
    }

    this.gridConfig.set(config);
  };

  private getSorting = (): SortOptions => {
    // Read directly from the service
    const sortField = this.gridSortingService.getSortFieldOrDefault();
    const sortOrder = this.gridSortingService.getSortDirectionOrDefault();
    return new SortOptions(sortField, sortOrder === -1);
  };
}
